import type { JSX } from "preact";
import { type HeroId, Role, ROLE_NAMES, type Slot } from "@rivalwatch/shared";
import { CLIENT_HEROES } from "../heroes/index.ts";

/**
 * Line-art ability glyphs (24×24, stroke = currentColor). One per ability name in
 * shared/heroes/defs.ts; unknown names fall back to a generic spark.
 */
const P: Record<string, JSX.Element> = {
  // Volt
  "Helix Rockets": (
    <>
      <path d="M7 16c0-5 2-9 5-12 3 3 5 7 5 12" />
      <path d="M9 16l-2 5M12 16v5M15 16l2 5" />
    </>
  ),
  Sprint: <path d="M3 6l6 6-6 6M10 6l6 6-6 6M17 6l4 6-4 6" />,
  "Biotic Field": (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5v9M7.5 12h9" />
    </>
  ),
  "Tactical Visor": (
    <>
      <circle cx="12" cy="12" r="7" />
      <circle cx="12" cy="12" r="2" />
      <path d="M12 1v5M12 18v5M1 12h5M18 12h5" />
    </>
  ),
  // Ember
  "Hover Jets": (
    <>
      <path d="M8 3v8M16 3v8M5 11h6M13 11h6" />
      <path d="M8 14l-1.5 4L8 21l1.5-3zM16 14l-1.5 4L16 21l1.5-3z" />
    </>
  ),
  "Jump Jet": <path d="M12 21V5M6 11l6-6 6 6M5 21h14" />,
  "Concussive Blast": (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v4M12 18v4M2 12h4M18 12h4M5 5l3 3M16 16l3 3M19 5l-3 3M8 16l-3 3" />
    </>
  ),
  Barrage: <path d="M4 20l5-9M10 21l3-11M16 20l1-10M20 17l-1-7M8 7l1 4 2-3M14 5l0 5 2-3" />,
  // Anvil
  Ward: <path d="M12 2l8 3v6c0 5-3.5 9-8 11-4.5-2-8-6-8-11V5z" />,
  Gateway: (
    <>
      <ellipse cx="7" cy="12" rx="3" ry="7" />
      <ellipse cx="17" cy="12" rx="3" ry="7" />
      <path d="M9.5 12h5M12.5 10l2 2-2 2" />
    </>
  ),
  Maelstrom: <path d="M12 12m-1 0a1 1 0 1 1 2 0a3 3 0 1 1-6 0a5 5 0 1 1 10 0a7 7 0 1 1-14 0a9 9 0 0 1 9-9" />,
  "Soul Sever": (
    <>
      <path d="M2 12s4-6 10-6 10 6 10 6-4 6-10 6S2 12 2 12z" />
      <circle cx="12" cy="12" r="2.5" />
      <path d="M12 1v3M12 20v3" />
    </>
  ),
  // Tether
  "Take a Breather": (
    <>
      <path d="M12 21s-8-5-8-11a4.5 4.5 0 0 1 8-3 4.5 4.5 0 0 1 8 3c0 6-8 11-8 11z" />
      <path d="M12 9v6M9 12h6" />
    </>
  ),
  "Chain Hook": <path d="M12 2v11a4 4 0 1 1-4-4M12 2h3" />,
  "Snare Trap": <path d="M3 15l3-5 3 5 3-5 3 5 3-5 3 5M3 15h18M6 19h12" />,
  "Whole Hog": <path d="M3 12h4M8 12l13-8M8 12h13M8 12l13 8M8 12l12-4M8 12l12 4" />,
  // Mender
  Bombard: (
    <>
      <path d="M4 7h11l3 2-3 2H4zM4 15h11l3 2-3 2H4z" />
      <path d="M20 9h2M20 17h2" />
    </>
  ),
  "Jet Dash": <path d="M13 5l7 7-7 7M20 12H8M3 8h5M2 12h3M3 16h5" />,
  "Rebirth Beacon": (
    <>
      <path d="M12 22V11M8 22h8M10 11h4" />
      <path d="M8.5 7.5a5 5 0 0 1 7 0M6 5a8.5 8.5 0 0 1 12 0" />
    </>
  ),
  Amplifier: (
    <>
      <path d="M3 9h4l5-5v16l-5-5H3z" />
      <path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13" />
    </>
  ),
  // Sprout
  Scope: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 3v6M12 15v6M3 12h6M15 12h6" />
    </>
  ),
  "Sleep Dart": <path d="M4 5h7l-7 8h7M14 12h6l-6 7h6" />,
  "Bio-Grenade": (
    <>
      <circle cx="12" cy="14" r="7" />
      <path d="M12 11v6M9 14h6M10 7V4h4v3M14 4l3-1" />
    </>
  ),
  Surge: <path d="M13 2L4 14h7l-1 8 9-12h-7z" />,
};

const FALLBACK = <path d="M12 3l2.5 6.5L21 12l-6.5 2.5L12 21l-2.5-6.5L3 12l6.5-2.5z" />;

/** A hero's own glyph for a slot (ClientHero.icons), else the named glyph, else the spark. */
export function AbilityIcon({ name, size = 26, hero, slot }: { name: string; size?: number; hero?: HeroId; slot?: Slot | "passive" }) {
  const own = hero !== undefined && slot !== undefined ? CLIENT_HEROES[hero]?.icons?.[slot] : undefined;
  const paths = own === undefined ? undefined : typeof own === "string" ? [own] : own;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      {paths ? paths.map((d, i) => <path key={i} d={d} />) : (P[name] ?? FALLBACK)}
    </svg>
  );
}

const ROLE_PATHS: Record<Role, JSX.Element> = {
  [Role.Tank]: (
    <>
      <path d="M12 2.8 20 6v5.7c0 4.8-3.2 8.2-8 9.8-4.8-1.6-8-5-8-9.8V6z" />
      <path d="M12 6.5v10M8.2 10.4h7.6" />
    </>
  ),
  [Role.Damage]: (
    <>
      <path d="M13 2 5 14h6l-1 8 9-13h-6z" />
      <path d="M4 5.5 7 7M20 18.5 17 17" />
    </>
  ),
  [Role.Support]: (
    <>
      <path d="M12 20s-7.5-4.7-7.5-10.3A4.2 4.2 0 0 1 12 7a4.2 4.2 0 0 1 7.5 2.7C19.5 15.3 12 20 12 20z" />
      <path d="M12 9.2v5.8M9.1 12.1h5.8" />
    </>
  ),
};

export function RoleIcon({ role, size = 18, label = false }: { role: Role; size?: number; label?: boolean }) {
  return (
    <svg
      class={`role-icon r${role}`}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2.2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden={label ? undefined : "true"}
      aria-label={label ? ROLE_NAMES[role] : undefined}
    >
      {ROLE_PATHS[role]}
    </svg>
  );
}
